export const deferred = <T>() => {
	let resolve!: (value: T | PromiseLike<T>) => void;
	let reject!: (reason?: any) => void;
	const promise = new Promise<T>((res, rej) => {
		resolve = res;
		reject = rej;
	});

	return {
		resolve,
		reject,
		promise
	};
};
export const delayPromise = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface WithRetryOptions {
	/** Максимальное количество попыток (включая первую). По умолчанию 3 */
	maxAttempts?: number;
	/** Базовая задержка в мс для exponential backoff. По умолчанию 1000 */
	baseDelayMs?: number;
	/** Максимальная задержка в мс. По умолчанию 5000 */
	maxDelayMs?: number;
	/** Callback для логирования retry */
	onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
}

/**
 * Выполняет асинхронную операцию с retry и exponential backoff с jitter.
 * При провале всех попыток пробрасывает последнюю ошибку.
 */
export async function withRetry<T>(
	fn: () => Promise<T>,
	options?: WithRetryOptions
): Promise<T> {
	const maxAttempts = options?.maxAttempts ?? 3;
	const baseDelayMs = options?.baseDelayMs ?? 1000;
	const maxDelayMs = options?.maxDelayMs ?? 5000;

	let lastError: unknown;

	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error;

			if (attempt === maxAttempts) {
				break;
			}

			// Exponential backoff с jitter: min(base * 2^(attempt-1), max) + random jitter
			const exponentialDelay = baseDelayMs * Math.pow(2, attempt - 1);
			const jitter = Math.random() * baseDelayMs * 0.5;
			const delayMs = Math.min(exponentialDelay + jitter, maxDelayMs);

			options?.onRetry?.(error, attempt, delayMs);

			await delayPromise(delayMs);
		}
	}

	throw lastError;
}
